import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { AccountApiError, createAccountApi } from '@/api'
import { getBrowserLanguage, normalizeLanguage } from '@/i18n'

import { useAccountData } from './useAccountData'
import { useStatusMessage } from './useStatusMessage'

export const useAccountApp = (tenantSlug: string | undefined) => {
    const api = useMemo(() => createAccountApi(tenantSlug), [tenantSlug])
    const accountKey = api.baseUrl

    const { status, showSuccess, showError, clearSuccess } = useStatusMessage()
    const { bootstrap, loading, bootstrapError, refreshBootstrap, refreshAll } = useAccountData({
        accountKey,
        api,
    })

    const language = normalizeLanguage(bootstrap?.language ?? getBrowserLanguage())
    const { t, i18n } = useTranslation()

    useEffect(() => {
        i18n.changeLanguage(language).catch(console.error)
    }, [i18n, language])

    const loginRedirectTriggeredRef = useRef(false)

    useEffect(() => {
        loginRedirectTriggeredRef.current = false
    }, [accountKey])

    useEffect(() => {
        if (!bootstrap?.enabled || !bootstrap.authenticated) {
            loginRedirectTriggeredRef.current = false
        }
    }, [bootstrap?.authenticated, bootstrap?.enabled])

    const login = useCallback(() => {
        if (!bootstrap) {
            return
        }

        const url = new URL(bootstrap.loginUrl)
        url.searchParams.set('returnTo', window.location.href)
        window.location.href = url.toString()
    }, [bootstrap])

    const reauthenticate = useCallback(
        (returnTo: string) => {
            if (!bootstrap) {
                return
            }

            const url = new URL(bootstrap.loginUrl)
            url.searchParams.set('reauthenticate', 'true')
            url.searchParams.set('returnTo', returnTo)
            window.location.href = url.toString()
        },
        [bootstrap]
    )

    const ensureFreshAuthentication = useCallback(
        async (returnTo: string) => {
            try {
                await api.checkAuthentication()
                return true
            } catch (error) {
                if (error instanceof AccountApiError && error.code === 'reauthenticationRequired') {
                    reauthenticate(returnTo)
                } else {
                    showError(error instanceof Error ? error.message : t('messages.loadError'))
                }

                return false
            }
        },
        [api, reauthenticate, showError, t]
    )

    useEffect(() => {
        if (!loading && bootstrap?.enabled && !bootstrap.authenticated && !loginRedirectTriggeredRef.current) {
            loginRedirectTriggeredRef.current = true
            login()
        }
    }, [bootstrap, loading, login])

    const logoutMutation = useMutation({
        mutationFn: api.logout,
        retry: false,
    })

    const handleLogout = useCallback(async () => {
        try {
            const result = await logoutMutation.mutateAsync()

            if (result.logoutUrl) {
                window.location.href = result.logoutUrl
                return
            }

            showSuccess(t('messages.signOutSuccess'))
            await refreshAll()
        } catch (error) {
            showError(error instanceof Error ? error.message : t('messages.signOutError'))
        }
    }, [logoutMutation, refreshAll, showError, showSuccess, t])

    return {
        accountKey,
        api,
        language,
        bootstrap,
        loading,
        bootstrapError,
        refreshBootstrap,
        reauthenticate,
        ensureFreshAuthentication,
        signingOut: logoutMutation.isPending,
        status,
        showSuccess,
        showError,
        clearSuccess,
        handleLogout,
    }
}
